/**
 * COIN VIEW — the world-space half of the coin mechanic (sim/coins.ts).
 *
 * ONE InstancedMesh sized to the sim's coin pool, and nothing else. Coins are
 * identical, unlit-ish, shadowless discs, so instancing is the whole story:
 * one draw call, one pipeline, zero per-frame allocation, and no per-spawn
 * object churn of the kind VehicleView had to be rewritten to avoid.
 *
 * ---------------------------------------------------------------------------
 * THE DISC
 *
 * A detailed little coin — milled edge, raised rim, sunken field, embossed
 * lightning bolt — built in code by render/coinGeometry.ts (read its header for
 * why it is code and not a generated GLB), `COIN_DIAMETER` across and
 * `COIN_THICKNESS` thick, arriving already rotated so the FACE points down the
 * road at the player. It then spins about the vertical axis, which is what
 * makes a coin read as a coin at speed: the silhouette pulses between a full
 * disc and a thin bar twice per turn, the mill twinkles as the ridges swing
 * through the sun, and a whole chain shimmers because each coin carries its own
 * phase (`CoinEntity.phase`, a deterministic spawn serial — not a draw).
 *
 * SIZE IS A VISIBILITY BUDGET, NOT A PROP MEASUREMENT (owner playtest,
 * 2026-08-02: "the coin is way too big, can't see past the traffic"). The coin
 * is 0.5 m across and flies at 0.42 m, i.e. entirely below the beltline of the
 * shortest vehicle in the fleet — a chain can never mask the car it is baiting
 * the player into. Far coins are ALLOWED to be a glint; what has to read is the
 * 15-30 m band where the lane decision is made, and 0.5 m subtends 11-23 px
 * there.
 *
 * The spin and the bob are driven from SIM TIME, not wall time, so they FREEZE
 * with the game on pause and cannot drift between the sim and the render.
 *
 * ---------------------------------------------------------------------------
 * TWO THINGS THAT ARE SCALE, NOT VISIBILITY
 *
 * Both are per-instance scale ramps, because a scale of 0 is a degenerate
 * triangle the rasteriser throws away — no `visible` toggles that would
 * reshuffle the instance buffer, and no `Material.transparent` flips (house
 * rule: those recompile a pipeline mid-run).
 *
 *  1. THE FAR RAMP. A chain is born wherever the physics puts it: the earliest
 *     anything can know about a vehicle is the frame it spawns, so the far end
 *     of a chain appears ~118 m ahead at run-start speeds and ~138 m at full
 *     ramp (sim/coins.ts). That is inside the draw distance, so a coin would
 *     POP. It scales in over [FADE_FAR, FADE_NEAR] instead — at 118-135 m a
 *     coin is barely a pixel across, so the ramp is invisible and the pop is
 *     gone, and the full-ramp case is simply born past FADE_FAR and drawn from
 *     scale 0 as it crosses it.
 *
 *  2. THE OCCLUSION SCALE. Traffic closes on the player faster than a coin does
 *     (player + vehicle speed against player speed alone), so a vehicle
 *     scheduled into the lane BETWEEN a chain and its own vehicle overtakes the
 *     chain during the approach and drives straight through it. The coin flies
 *     at 0.42 m and every vehicle's body reaches the road, so that is still a
 *     visible clip even at the lowered height (it is a clip THROUGH the car's
 *     flank rather than across its windows). Any
 *     coin currently inside a live vehicle's box is scaled away — it reads as
 *     the coin passing behind the car, which is what is physically happening.
 *
 * ---------------------------------------------------------------------------
 * WARM-UP
 *
 * `warmup()` parks the mesh full of visible instances in front of the camera so
 * its colour AND shadow-pass pipelines compile under the loading overlay. It
 * does NOT cast (a shadowless pickup is both cheaper and clearer against the
 * road), but the mesh is still walked by the shadow pass, so it is warmed like
 * everything else. See render/warmup.ts for why any first-draw-at-runtime is a
 * multi-frame hitch.
 */

import { InstancedMesh, Object3D, type BufferGeometry, type Material } from "three/webgpu";
import { buildCoinGeometry, COIN_DIAMETER, COIN_THICKNESS } from "./coinGeometry";
import { TUNING } from "../sim/tuning";
import type { WorldState } from "../sim/types";
import { plasticMaterial } from "./plastic";
import { curveOffset } from "./curve";
import { isLive } from "../sim/vehicles";
import { modelLength } from "../sim/vehicles";

const C = TUNING.coins;

// Diameter and thickness live with the mesh that is built from them
// (render/coinGeometry.ts) and are re-exported here for existing consumers.
export { COIN_DIAMETER, COIN_THICKNESS };

/** Gold. Candy plastic, with a warm emissive floor so it glows in the tunnel. */
const COIN_COLOR = 0xffc93c;
const COIN_EMISSIVE = 0x5a3c00;

/** Turns per second of the vertical-axis spin. */
const SPIN_RATE = 0.85;
/**
 * Bob amplitude (m) and rate (Hz) about `TUNING.coins.height`.
 *
 * SMALL, because the coin now flies LOW (centre 0.42 m, see tuning). The disc
 * stands on edge, so it reaches COIN_DIAMETER/2 = 0.25 m below its centre: at
 * the bob minimum (0.42 - 0.06 = 0.36 m) the bottom of the coin is still
 * 0.11 m clear of the asphalt. That clearance is the constraint on this number
 * — a bigger bob would sink the chain into the road.
 */
const BOB_AMPLITUDE = 0.06;
const BOB_RATE = 0.55;

/**
 * Far scale ramp (m ahead): fully hidden beyond FADE_FAR, full size at
 * FADE_NEAR. See the header — this exists because a chain's far end is born
 * inside the draw distance at run-start speeds.
 */
const FADE_FAR = 135;
const FADE_NEAR = 95;

/** Reused matrix compositor — never allocate per frame. */
const _dummy = new Object3D();

/**
 * The pooled coin field renderer. Add `mesh` to the scene; call `sync` once per
 * render frame with the sim state and the interpolated travel distance.
 */
export class CoinView {
  readonly mesh: InstancedMesh;
  private readonly geometry: BufferGeometry;
  private readonly material: Material;

  constructor(capacity: number = C.poolSize) {
    // A milled-edge, raised-rim, bolt-embossed coin — already oriented with its
    // FACE down the road at the player. See render/coinGeometry.ts.
    this.geometry = buildCoinGeometry();
    this.material = plasticMaterial({
      color: COIN_COLOR,
      name: "coin",
      kind: "candy",
    });
    // A warm emissive floor: the coin keeps reading as gold in the tunnel biome
    // (which drops the sun to a fraction of its outdoor intensity) and picks up
    // a faint bloom-free glow outdoors. Written once, at build — never toggled
    // at runtime, so it cannot cause a pipeline recompile.
    (this.material as unknown as { emissive: { setHex(h: number): void } }).emissive.setHex(
      COIN_EMISSIVE,
    );

    this.mesh = new InstancedMesh(this.geometry, this.material, capacity);
    // The instance matrices are rewritten every frame and the coins span the
    // whole draw distance, so a bounding-sphere-based cull would be both wrong
    // and wasted work; `mesh.count` is the real limiter.
    this.mesh.frustumCulled = false;
    // Shadowless ON PURPOSE: a floating pickup with a hard contact shadow reads
    // as an object lying on the road, and the shadow pass is the expensive half
    // of every caster in the scene.
    this.mesh.castShadow = false;
    this.mesh.receiveShadow = false;
    this.mesh.count = 0;
  }

  /**
   * Write one instance per live coin.
   *
   * @param state     the sim state (coins, vehicles, phase).
   * @param distance  interpolated player travel — the render-only lateral curve
   *                  offset is a function of it, exactly as for vehicles.
   */
  sync(state: WorldState, distance: number): void {
    const coins = state.coins;
    const vehicles = state.vehicles;
    // SIM TIME drives the animation, so pausing freezes the spin and the bob.
    const t = state.time;
    let n = 0;
    for (let i = 0; i < coins.length; i++) {
      const c = coins[i];
      if (!c.active) continue;

      // (1) the far ramp.
      const ahead = -c.z;
      let scale = 1;
      if (ahead > FADE_NEAR) {
        scale = (FADE_FAR - ahead) / (FADE_FAR - FADE_NEAR);
        if (scale <= 0) continue; // fully beyond the ramp — draw nothing at all
        if (scale > 1) scale = 1;
      }

      // (2) the occlusion scale: a vehicle that overtook this coin is currently
      //     sitting on top of it. Only same-lane vehicles can, so the lateral
      //     test rejects almost everything before the z test runs.
      let hidden = false;
      for (let v = 0; v < vehicles.length; v++) {
        const veh = vehicles[v];
        if (!isLive(veh)) continue;
        if (Math.abs(veh.x - c.x) > TUNING.road.laneWidth * 0.5) continue;
        const half = modelLength(veh.model) / 2;
        if (c.z > veh.z - half && c.z < veh.z + half) {
          hidden = true;
          break;
        }
      }
      if (hidden) continue;

      const phase = c.phase;
      _dummy.position.set(
        c.x + curveOffset(c.z, distance),
        C.height + BOB_AMPLITUDE * Math.sin((t * BOB_RATE + phase) * Math.PI * 2),
        c.z,
      );
      _dummy.rotation.set(0, (t * SPIN_RATE + phase) * Math.PI * 2, 0);
      _dummy.scale.setScalar(scale);
      _dummy.updateMatrix();
      this.mesh.setMatrixAt(n, _dummy.matrix);
      n++;
      if (n >= this.mesh.instanceMatrix.count) break;
    }
    this.mesh.count = n;
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  /**
   * Compile this mesh's pipelines under the loading overlay: park a full set of
   * instances in front of the camera and let the caller render a frame. One
   * pass is enough — every coin is the same geometry and the same material
   * instance, so there is exactly one (colour, shadow) pipeline pair.
   */
  warmup(x: number, y: number, z: number): CoinWarmup {
    const cap = this.mesh.instanceMatrix.count;
    const beginPass = (): void => {
      for (let i = 0; i < cap; i++) {
        _dummy.position.set(x + (i % 5) * 0.7, y + 1.2, z - Math.floor(i / 5) * 0.7);
        _dummy.rotation.set(0, i * 0.3, 0);
        _dummy.scale.setScalar(1);
        _dummy.updateMatrix();
        this.mesh.setMatrixAt(i, _dummy.matrix);
      }
      this.mesh.count = cap;
      this.mesh.instanceMatrix.needsUpdate = true;
    };
    const restore = (): void => {
      this.mesh.count = 0;
      this.mesh.instanceMatrix.needsUpdate = true;
    };
    return { passes: 1, beginPass, restore };
  }

  dispose(): void {
    this.mesh.dispose();
    this.geometry.dispose();
    this.material.dispose();
  }
}

/** Handle returned by {@link CoinView.warmup}. */
export interface CoinWarmup {
  readonly passes: number;
  beginPass(p: number): void;
  restore(): void;
}

/**
 * The far scale ramp, extracted so it can be unit-tested without a GPU.
 * Returns the instance scale for a coin `ahead` metres in front of the player:
 * 0 past FADE_FAR (draw nothing), 1 at or inside FADE_NEAR.
 */
export function coinFarScale(ahead: number): number {
  if (ahead <= FADE_NEAR) return 1;
  const s = (FADE_FAR - ahead) / (FADE_FAR - FADE_NEAR);
  return s <= 0 ? 0 : s > 1 ? 1 : s;
}
